/**
 * Parlay Quick Stats Grid
 * Summary stats for parlay-level analytics
 */
import { formatPercent } from '@parlay-picker/shared/formatting';
import type { ReactElement } from 'react';
import type { ParlayDashboardSummary } from '../hooks/useLabDashboard';

interface ParlayQuickStatsGridProps {
  summary: ParlayDashboardSummary;
}

export function ParlayQuickStatsGrid({ summary }: ParlayQuickStatsGridProps): ReactElement {
  const formatUnits = (units: number): string => {
    const sign = units >= 0 ? '+' : '';
    return `${sign}${units.toFixed(2)}u`;
  };

  return (
    <div className="grid grid-cols-2 @3xl:grid-cols-4 gap-4">
      {/* Total Parlays */}
      <div className="bg-theme-surface rounded-lg p-4">
        <div className="text-theme-text-secondary text-xs uppercase tracking-wide mb-1">Total</div>
        <div className="text-2xl font-bold text-white">{summary.totalParlays}</div>
        <div className="text-theme-text-tertiary text-xs mt-1">
          {summary.settledParlays} settled, {summary.pending} pending
        </div>
      </div>

      {/* Win Rate */}
      <div className="bg-theme-surface rounded-lg p-4">
        <div className="text-theme-text-secondary text-xs uppercase tracking-wide mb-1">
          Win Rate
        </div>
        <div
          className={`text-2xl font-bold ${summary.winRate >= 0.5 ? 'text-theme-success' : 'text-theme-danger'}`}
        >
          {formatPercent(summary.winRate)}
        </div>
        <div className="text-theme-text-tertiary text-xs mt-1">
          {summary.wins}W - {summary.losses}L{summary.pushes > 0 && ` - ${summary.pushes}P`}
        </div>
      </div>

      {/* Units */}
      <div className="bg-theme-surface rounded-lg p-4">
        <div className="text-theme-text-secondary text-xs uppercase tracking-wide mb-1">Units</div>
        <div
          className={`text-2xl font-bold ${summary.units >= 0 ? 'text-theme-success' : 'text-theme-danger'}`}
        >
          {formatUnits(summary.units)}
        </div>
        <div className="text-theme-text-tertiary text-xs mt-1">Net profit/loss</div>
      </div>

      {/* Settled */}
      <div className="bg-theme-surface rounded-lg p-4">
        <div className="text-theme-text-secondary text-xs uppercase tracking-wide mb-1">
          Settled
        </div>
        <div className="text-2xl font-bold text-white">{summary.settledParlays}</div>
        <div className="text-theme-text-tertiary text-xs mt-1">
          {summary.pushes > 0 ? `${summary.pushes} pushed` : 'No pushes'}
        </div>
      </div>
    </div>
  );
}
